import { useState } from 'react';
import type { AnalyticsBootstrap, Dataset } from '@lognai/plugin-analytics';
import { Badge, Button, Card, Input } from '@lognai/synapse-design-system-v2';
import { Database, Search, Check } from '@lognai/synapse-design-system-v2/icons';
import { Link } from 'react-router-dom';
import { businessLabel } from '../../lib/user-facing';
import { datasetViewFor } from '../creator-helpers';
import { visualDate } from '../VisualSources';

export function SourcePicker({
  catalog,
  datasets,
  view,
  onChange,
  disabled,
  warning,
}: {
  catalog: AnalyticsBootstrap['semanticCatalog'];
  datasets: Dataset[];
  view: string;
  onChange: (view: string) => void;
  disabled?: boolean;
  warning?: string;
}) {
  const [open, setOpen] = useState(!view);
  const [search, setSearch] = useState('');
  const dataset = datasets.find((item) => datasetViewFor(item.id) === view);
  const name =
    catalog[view]?.title ??
    (view.startsWith('dataset_')
      ? 'Dataset unavailable'
      : view
        ? businessLabel(view)
        : 'Choose data source');
  const matches = Object.entries(catalog).filter(([key, item]) =>
    `${item.title ?? businessLabel(key)} ${key.startsWith('dataset_') ? 'dataset' : 'business module'}`
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-4 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="rounded-lg bg-primary-subtle p-2 text-primary">
            <Database size={20} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">Data source</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h2 className="break-words text-base font-semibold">{name}</h2>
              {dataset && (
                <>
                  <Badge variant="outline">Version {dataset.activeVersion}</Badge>
                  <Badge variant="outline">
                    {dataset.visibility === 'company' ? 'Company' : 'Private'}
                  </Badge>
                </>
              )}
            </div>
            {dataset ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {dataset.rows.toLocaleString()} stored rows · Source date:{' '}
                {visualDate(dataset.sourceDate)} · Published: {visualDate(dataset.publishedAt)}
              </p>
            ) : (
              view && (
                <p className="mt-1 text-xs text-muted-foreground">Current governed business data</p>
              )
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {dataset && (
            <Link
              className="text-xs text-primary hover:underline"
              to={`/analytics/datasets?dataset=${encodeURIComponent(dataset.id)}`}
            >
              View dataset
            </Link>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            aria-expanded={open}
            aria-controls="chart-source-options"
            onClick={() => setOpen((value) => !value)}
          >
            {view ? 'Change source' : 'Choose data source'}
          </Button>
        </div>
      </div>
      {dataset && (
        <p className="border-t border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          Each row represents: {dataset.grain}. File changes appear after a new version is
          published.
        </p>
      )}
      {warning && (
        <p role="status" className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          {warning}
        </p>
      )}
      {open && (
        <div id="chart-source-options" className="space-y-3 border-t border-border p-4">
          <div className="relative">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-3 text-muted-foreground"
            />
            <Input
              autoFocus
              aria-label="Search data sources"
              className="pl-9"
              placeholder="Search business modules and datasets"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              { name: 'Business modules', dataset: false },
              { name: 'My datasets', dataset: true },
            ].map((group) => (
              <section key={group.name} className="min-w-0">
                <h3 className="mb-2 text-xs font-semibold text-muted-foreground">{group.name}</h3>
                <div className="max-h-48 space-y-1 overflow-y-auto">
                  {matches
                    .filter(([key]) => key.startsWith('dataset_') === group.dataset)
                    .map(([key, item]) => (
                      <button
                        key={key}
                        type="button"
                        disabled={disabled}
                        aria-pressed={view === key}
                        className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm focus-visible:ring-2 focus-visible:ring-ring ${view === key ? 'border-primary bg-primary-subtle text-primary' : 'border-border hover:bg-muted'}`}
                        onClick={() => {
                          onChange(key);
                          setOpen(false);
                          setSearch('');
                        }}
                      >
                        <span className="truncate">{item.title ?? businessLabel(key)}</span>
                        {view === key && <Check size={14} />}
                      </button>
                    ))}
                  {!matches.some(([key]) => key.startsWith('dataset_') === group.dataset) && (
                    <p className="p-2 text-xs text-muted-foreground">
                      {group.dataset
                        ? 'No available datasets match.'
                        : 'No business modules match.'}
                    </p>
                  )}
                </div>
              </section>
            ))}
          </div>
          <Link
            to="/analytics/datasets"
            className="inline-block text-xs text-primary hover:underline"
          >
            Manage or import datasets
          </Link>
        </div>
      )}
    </Card>
  );
}
